/**
 * Client-side wrapper around the Supabase Edge Function `extract`.
 *
 * SECURITY: Calls a server endpoint. The Anthropic API key NEVER touches the client.
 * Authenticated calls only — the function rejects requests without a valid JWT.
 * See CLAUDE.md hard rule #1 and PRD §10 S1.
 */

import type { SchemaId } from "./schemas";
import { supabase, isSupabaseConfigured } from "./supabase";
import { getCreditBalance } from "./billing";

/**
 * Plan quotas — MUST stay in sync with PLAN_QUOTAS in
 * supabase/functions/extract/index.ts (the server is the source of truth;
 * this copy only powers proactive client-side UX).
 */
export const PLAN_QUOTAS: Record<string, { limit: number; window: "day" | "month" }> = {
  free: { limit: 1, window: "day" },
  starter: { limit: 50, window: "month" },
  pro: { limit: 200, window: "month" },
  business: { limit: 1000, window: "month" },
};

export interface UsageStatus {
  plan: string;
  used: number;
  limit: number;
  window: "day" | "month";
  creditsRemaining: number;
  /** True when the plan quota is exhausted AND no credits remain to fall back on. */
  blocked: boolean;
}

/**
 * Check the signed-in user's current quota usage so the UI can show a
 * usage badge and pre-emptively wall them BEFORE they stage a file and
 * click Process. The server still re-checks on every extract call — this
 * is purely a UX shortcut, not a security boundary.
 *
 * Returns null in mock mode or when not signed in.
 */
export async function getUsageStatus(): Promise<UsageStatus | null> {
  if (!isSupabaseConfigured) return null;
  const { data: sessionData } = await supabase.auth.getSession();
  if (!sessionData.session) return null;

  const { data: account } = await supabase.from("accounts").select("plan").maybeSingle();
  const plan = (account?.plan as string) ?? "free";
  const quota = PLAN_QUOTAS[plan] ?? PLAN_QUOTAS.free;

  // Window start in UTC — must mirror extract/index.ts exactly.
  const now = new Date();
  const windowStart =
    quota.window === "day"
      ? new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()))
      : new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));

  const { count } = await supabase
    .from("extractions")
    .select("id", { count: "exact", head: true })
    .gte("created_at", windowStart.toISOString());

  const used = count ?? 0;
  const creditsRemaining = await getCreditBalance();

  return {
    plan,
    used,
    limit: quota.limit,
    window: quota.window,
    creditsRemaining,
    blocked: used >= quota.limit && creditsRemaining <= 0,
  };
}

/** Result shape returned by /extract. Loose typing — the server validates against the schema. */
export interface ExtractResult {
  extraction_id: string;
  status: "completed" | "failed";
  schema: SchemaId;
  result: Record<string, unknown> | null;
  confidence: number | null;
  pages_processed: number;
  processing_ms: number;
  /** "plan" = charged against monthly/daily quota. "credit" = charged a top-up credit instead. */
  billing?: "plan" | "credit";
  usage?: {
    documents_used: number;
    documents_limit: number;
    window: "day" | "month";
    credits_remaining?: number | null;
  };
  error?: { code: string; message: string };
}

export interface ExtractOptions {
  file: File;
  schema: SchemaId;
}

export class ExtractError extends Error {
  code: string;
  constructor(code: string, message: string) {
    super(message);
    this.code = code;
    this.name = "ExtractError";
  }
}

/**
 * Production: POSTs to Supabase Edge Function `extract`, authenticated with the user's JWT.
 * Dev (no env vars set): returns a mock result so the UI is runnable before Supabase is wired.
 */
export async function extractDocument({ file, schema }: ExtractOptions): Promise<ExtractResult> {
  if (!isSupabaseConfigured) {
    return mockExtract(file, schema);
  }

  // Require an active session — the Edge Function rejects anonymous uploads in 1.5.
  const { data: sessionData } = await supabase.auth.getSession();
  const accessToken = sessionData.session?.access_token;
  if (!accessToken) {
    throw new ExtractError("not_authenticated", "Sign in to upload documents.");
  }

  const url = import.meta.env.VITE_SUPABASE_URL as string;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

  const formData = new FormData();
  formData.append("file", file);
  formData.append("schema", schema);

  // Abort if the extract function doesn't respond within 120s. Claude Vision
  // can legitimately take 60-90s on a multi-page scanned PDF, but anything past
  // 2 minutes is almost certainly stuck — better to surface an error than to
  // leave the user staring at a spinner forever.
  const controller = new AbortController();
  const timeoutMs = 120_000;
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  let response: Response;
  try {
    response = await fetch(`${url}/functions/v1/extract`, {
      method: "POST",
      headers: {
        // Authorization carries the USER JWT — Edge Function reads auth.uid() from it.
        Authorization: `Bearer ${accessToken}`,
        apikey: anonKey,
      },
      body: formData,
      signal: controller.signal,
    });
  } catch (err) {
    if ((err as { name?: string })?.name === "AbortError") {
      throw new ExtractError(
        "timeout",
        "Processing took longer than 2 minutes. The file may be too large or complex — try a smaller or clearer document, or come back and try again.",
      );
    }
    throw new ExtractError(
      "network_error",
      err instanceof Error ? err.message : "Network request failed. Check your connection and try again.",
    );
  } finally {
    clearTimeout(timeoutId);
  }

  const body = await response.json().catch(() => null);

  if (!response.ok || !body) {
    const code = body?.error?.code ?? "request_failed";
    const message = body?.error?.message ?? `Request failed with status ${response.status}`;
    throw new ExtractError(code, message);
  }

  if (body.status === "failed" && body.error) {
    throw new ExtractError(body.error.code, body.error.message);
  }

  return body as ExtractResult;
}

/** Stand-in for local dev before Supabase is configured. */
async function mockExtract(file: File, schema: SchemaId): Promise<ExtractResult> {
  await new Promise((r) => setTimeout(r, 1500));
  const mockResults: Record<SchemaId, Record<string, unknown>> = {
    receipt: {
      vendor: "Whole Foods Market",
      date: "2026-05-10",
      total: 47.83,
      currency: "USD",
      tax: 3.24,
      line_items: [
        { name: "Organic Bananas", qty: 1, unit_price: 1.49 },
        { name: "Sourdough Bread", qty: 1, unit_price: 5.99 },
      ],
    },
    invoice: {
      invoice_number: "INV-2026-0042",
      issue_date: "2026-05-01",
      due_date: "2026-05-31",
      vendor: "Acme Consulting LLC",
      bill_to: "Trijazel, Inc.",
      total: 4500.0,
      currency: "USD",
      line_items: [{ description: "Senior dev review (May)", qty: 1, unit_price: 4500.0 }],
    },
    business_card: {
      name: "Stephen O'Leary",
      title: "Founder & Senior Dev",
      company: "Trijazel",
      email: "stephen@trijazel.com",
      phone: "+1-813-555-0100",
      website: "trijazel.com",
    },
    free_text: {
      text: "[Mock] Connect Supabase + Anthropic to see real OCR results.",
      language: "en",
    },
    pdf_to_text: {
      title: "[Mock] Quarterly Report",
      blocks: [
        { type: "heading", level: 1, content: "[Mock] Quarterly Report" },
        { type: "paragraph", content: "Connect Supabase + Anthropic to see real PDF text extraction." },
        { type: "heading", level: 2, content: "Highlights" },
        { type: "list", content: "- Structured blocks preserved\n- Headings tagged with level\n- Lists kept as lists" },
      ],
      language: "en",
      page_count: 1,
    },
  };

  void file;
  return {
    extraction_id: `mock_${Date.now()}`,
    status: "completed",
    schema,
    result: mockResults[schema],
    confidence: 0.92,
    pages_processed: 1,
    processing_ms: 1500,
  };
}

export function isMockMode() {
  return !isSupabaseConfigured;
}
